import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { currentLang, istCreatorKonto } from "../../lib/sprache";

/** Befehlspalette (dialog#command aus dem Design): ⌘K / Strg+K, Suche filtert, Escape schließt. */
export interface PaletteItem {
  label: string;
  to: string;
  hint?: string;
  /** zweiter Weg zum Treffer, etwa „Vorschau“ eines Vertrags (K-511) */
  extra?: { label: string; to: string; aria?: string };
}

let openPalette: () => void = () => undefined;
export function showCommandPalette() {
  openPalette();
}

/** Vergleich ohne Groß-/Kleinschreibung und ohne Umlaut-Hürde („uebersicht“ findet „Übersicht“; Praxistest K-121) */
const glatt = (s: string) => s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");

export function CommandPalette({ items, lang = currentLang(), search }: {
  items: PaletteItem[]; lang?: "de" | "en";
  /** Suche in den Daten (Creator, Marken, Kooperationen, Rechnungen, Verträge); ohne Angabe nur Seiten */
  search?: (q: string) => Promise<PaletteItem[]>;
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const ref = useRef<HTMLDialogElement>(null);
  const [query, setQuery] = useState("");
  useEffect(() => {
    openPalette = () => {
      setQuery("");
      ref.current?.showModal();
      ref.current?.querySelector("input")?.focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openPalette();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const shown = useMemo(() => {
    const q = glatt(query.trim());
    return q ? items.filter((i) => glatt(`${i.label} ${i.hint ?? ""}`).includes(q)) : items;
  }, [items, query]);
  // Treffer aus den Daten: kurz warten, bis die Eingabe steht; eine ältere Antwort überschreibt keine neuere
  const [treffer, setTreffer] = useState<PaletteItem[]>([]);
  const [sucht, setSucht] = useState(false);
  const lauf = useRef(0);
  const searchRef = useRef(search);
  searchRef.current = search;
  useEffect(() => {
    const q = query.trim();
    const nr = ++lauf.current;
    if (!searchRef.current || q.length < 2) { setTreffer([]); setSucht(false); return; }
    setSucht(true);
    const timer = window.setTimeout(() => {
      void searchRef.current!(q).then((r) => { if (lauf.current === nr) { setTreffer(r); setSucht(false); } }).catch(() => { if (lauf.current === nr) { setTreffer([]); setSucht(false); } });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  const close = () => ref.current?.close();
  return (
    <dialog id="command" ref={ref} aria-labelledby="command-title">
      <div className="stack">
        <div className="tk-dialog-head">
          <h2 id="command-title">{t("Schnell zu …", "Go to …")}</h2>
          <button className="" onClick={close}>{t("Schließen", "Close")}</button>
        </div>
        <label>
          {search ? t("Seiten und Einträge suchen", "Search pages and records") : t("Seiten suchen", "Search pages")}
          <input aria-label={search ? t("Seiten und Einträge suchen", "Search pages and records") : t("Seiten suchen", "Search pages")} placeholder={istCreatorKonto() ? t("Deals, Rechnungen, Marken …", "Deals, invoices, brands …") : t("Kooperationen, Rechnungen, Creator …", "Deals, invoices, contracts …")} value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        {shown.map((i) => (
          <Link key={i.to + i.label} className="btn" to={i.to} onClick={close}>
            {i.label}
          </Link>
        ))}
        {treffer.length > 0 && <div className="tk-suche-gruppe">{t("Gefunden", "Found")}</div>}
        {treffer.map((i) => {
          const link = (
            <Link key={i.to} className="btn tk-suche-treffer" to={i.to} onClick={close}>
              <span>{i.label}</span>{i.hint && <small>{i.hint}</small>}
            </Link>
          );
          return i.extra ? (
            <div key={i.to} className="tk-suche-zeile">
              {link}
              <Link className="btn" to={i.extra.to} onClick={close} aria-label={i.extra.aria}>{i.extra.label}</Link>
            </div>
          ) : link;
        })}
        {sucht && treffer.length === 0 && <p className="muted" role="status">{t("Suche läuft …", "Searching …")}</p>}
        {shown.length === 0 && treffer.length === 0 && !sucht && <p className="muted">{t("Keine passenden Ergebnisse.", "No matching results.")}</p>}
      </div>
    </dialog>
  );
}
